/* eslint-disable multiline-ternary */
import {
  Button,
  Image,
  useToast,
  type ButtonProps
} from "@chakra-ui/react"
import { buildDocumentUrl } from "../../common/documentUrl"
import { useGetInstallationInstructionReportLazyQuery, useGetReportLazyQuery } from "../../type-and-hooks"
import { newFileIconImg, pdfPrimaryColorImg } from "../images"

export const PDFIcon = (): JSX.Element => <Image src={pdfPrimaryColorImg} w="15px" />
const NewFileIcon = (): JSX.Element => <Image src={newFileIconImg} pos="relative" w="24px" left={"-3px"} mr="0" pr="0" />

interface PDFLinkButtonProps {
  text: string
  reportNo?: string
  installationReport?: string
  url?: string
  fileName?: string | null
  iconPos?: "left" | "right"
  props?: ButtonProps
}

const commonProps = { variant: "linkButton", textDecoration: "none", fontWeight: "400px" }

export const PDFLinkButton = ({ text, reportNo, installationReport, url, fileName, iconPos = "left", props }: PDFLinkButtonProps): JSX.Element => {
  const toast = useToast()
  const [getReport, { loading: reportLoading }] = useGetReportLazyQuery()
  const [getInstallationReport, { loading: installationLoading }] = useGetInstallationInstructionReportLazyQuery()

  const loading = reportLoading || installationLoading

  const openPreview = (fetchedUrl: string): void => {
    window.open(fetchedUrl, "_blank", "noopener,noreferrer")
  }

  const handleClick = async (): Promise<void> => {
    if (props?.onClick) {
      return
    }

    if (url) {
      openPreview(buildDocumentUrl(fileName, url) ?? url)
      return
    }

    if (installationReport) {
      try {
        const { data } = await getInstallationReport({ variables: { instruction: installationReport } })
        const fetchedUrl = data?.getInstallationInstructionReport?.reportURL ?? null
        if (!fetchedUrl) throw new Error("Installation report URL not found")
        openPreview(buildDocumentUrl(data?.getInstallationInstructionReport?.fileName, fetchedUrl) ?? fetchedUrl)
      } catch {
        toast({
          title: "Preview Failed",
          description: `Failed to load ${text}. Please try again.`,
          status: "error",
          duration: 5000,
          isClosable: true
        })
      }
      return
    }

    if (reportNo) {
      try {
        const { data } = await getReport({ variables: { reportNo } })
        const fetchedUrl = data?.getReport?.reportURL ?? null
        if (!fetchedUrl) throw new Error("Report URL not found")
        openPreview(buildDocumentUrl(data?.getReport?.fileName, fetchedUrl) ?? fetchedUrl)
      } catch {
        toast({
          title: "Preview Failed",
          description: `Failed to load ${text}. Please try again.`,
          status: "error",
          duration: 5000,
          isClosable: true
        })
      }
    }
  }

  return (
    <>
      {iconPos === "left" ? (
        <Button
          {...commonProps}
          textAlign={"left"}
          leftIcon={<PDFIcon />}
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          my="2px"
          {...props}
          onClick={props?.onClick ?? handleClick}
          isLoading={loading}
          cursor={reportNo ?? installationReport ?? url ?? props?.onClick ? "pointer" : "default"}
        >
          {text}
        </Button>
      ) : (
        <Button
          {...commonProps}
          textAlign={"left"}
          rightIcon={<PDFIcon />}
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          my="2px"
          {...props}
          onClick={props?.onClick ?? handleClick}
          isLoading={loading}
          cursor={reportNo ?? installationReport ?? url ?? props?.onClick ? "pointer" : "default"}
        >
          {text}
        </Button>
      )}
    </>
  )
}

export const NewFileLinkButton = ({ text, iconPos = "left", props }: PDFLinkButtonProps): JSX.Element => (
  <>
    {iconPos === "left" ? (
      <Button {...commonProps} textAlign={"left"} {...props} display="flex" alignItems="center" justifyContent="flex-start" my="2px">
        <NewFileIcon />
        {text}
      </Button>
    ) : (
      <Button
        {...commonProps}
        textAlign={"left"}
        rightIcon={<NewFileIcon />}
        {...props}
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        my="2px"
      >
        {text}
      </Button>
    )}
  </>
)
